import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// ─── Types ───
interface TourState {
  completed: boolean;
  skipped: boolean;
  currentStep: number;
  lastSeenAt: string | null;
  version: string;
}

interface TourContextValue {
  running: boolean;
  stepIndex: number;
  tourState: TourState;
  startTour: () => void;
  stopTour: () => void;
  skipTour: () => void;
  resetTour: () => void;
  setStepIndex: (index: number) => void;
  completeTour: () => void;
}

// ─── Constants ───
const TOUR_STORAGE_KEY = 'japexpress:tour-state';
const TOUR_VERSION = '1.0.0'; // Increment para invalidar tours antigos

const DEFAULT_TOUR_STATE: TourState = {
  completed: false,
  skipped: false,
  currentStep: 0,
  lastSeenAt: null,
  version: TOUR_VERSION,
};

/**
 * Lê o estado do tour do localStorage de forma SÍNCRONA.
 * Usado como lazy initializer do useState para evitar que o useEffect de
 * persistência sobrescreva o estado salvo antes de carregá-lo (bug StrictMode).
 */
function initTourState(): TourState {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!stored) return DEFAULT_TOUR_STATE;

    const parsed: TourState = JSON.parse(stored);

    if (parsed.version !== TOUR_VERSION) {
      console.log('[Tour] versão diferente, resetando estado');
      return DEFAULT_TOUR_STATE;
    }

    console.log('[Tour] estado carregado do localStorage:', parsed);
    return parsed;
  } catch (error) {
    console.error('[Tour] erro ao carregar estado:', error);
    return DEFAULT_TOUR_STATE;
  }
}

// ─── Context ───
const TourContext = createContext<TourContextValue | undefined>(undefined);

// ─── Provider ───
export function TourProvider({ children }: { children: ReactNode }) {
  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Inicialização SÍNCRONA do localStorage via lazy initializer.
  // Isso evita o bug onde o useEffect de persistência roda com o estado default
  // e sobrescreve 'completed: true' antes do useEffect de carregamento corrigir.
  const [tourState, setTourState] = useState<TourState>(initTourState);

  // Persistir estado no localStorage sempre que mudar.
  // Como tourState é inicializado diretamente do localStorage, esta escrita
  // nunca sobrescreve um estado válido com o estado default.
  useEffect(() => {
    console.log('[Tour] persistindo no localStorage:', tourState);
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(tourState));
  }, [tourState]);

  const startTour = () => {
    // Nunca reiniciar um tour já concluído ou pulado
    const current = localStorage.getItem(TOUR_STORAGE_KEY);
    if (current) {
      try {
        const state: TourState = JSON.parse(current);
        if (state.completed || state.skipped) {
          console.log('[Tour] startTour ignorado — tour já concluído/pulado');
          return;
        }
      } catch { /* ignora */ }
    }

    console.log('[Tour] iniciando tour');
    setRunning(true);
    setStepIndex(0);
    setTourState((prev) => ({ ...prev, currentStep: 0 }));
  };

  const stopTour = () => {
    setRunning(false);
  };

  const skipTour = () => {
    console.log('[Tour] PULANDO tour — gravando skipped: true no localStorage AGORA');
    setRunning(false);
    setTourState((prev) => {
      const next: TourState = { ...prev, skipped: true, lastSeenAt: new Date().toISOString() };
      // Escrita SÍNCRONA — garante que o localStorage é atualizado imediatamente,
      // antes de qualquer re-render ou effect posterior
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
      console.log('[Tour] skipTour gravou no localStorage:', next);
      return next;
    });
  };

  const completeTour = () => {
    console.log('[Tour] CONCLUINDO tour — gravando completed: true no localStorage AGORA');
    setRunning(false);
    setTourState((prev) => {
      const next: TourState = { ...prev, completed: true, lastSeenAt: new Date().toISOString() };
      // Escrita SÍNCRONA — garante que o localStorage é atualizado imediatamente,
      // antes de qualquer re-render ou effect posterior
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
      console.log('[Tour] completeTour gravou no localStorage:', next);
      return next;
    });
  };

  const resetTour = () => {
    console.log('[Tour] resetando tour');
    setTourState(DEFAULT_TOUR_STATE);
    setStepIndex(0);
    setRunning(false);
    localStorage.removeItem(TOUR_STORAGE_KEY);
  };

  const value: TourContextValue = {
    running,
    stepIndex,
    tourState,
    startTour,
    stopTour,
    skipTour,
    resetTour,
    setStepIndex,
    completeTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

// ─── Hook ───
export function useTourContext() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext deve ser usado dentro de TourProvider');
  }
  return context;
}

// ─── Helper: Verificar se deve mostrar tour ───
export function shouldShowTour(): boolean {
  const stored = localStorage.getItem(TOUR_STORAGE_KEY);
  if (!stored) return true;

  try {
    const state: TourState = JSON.parse(stored);

    if (state.completed || state.skipped) {
      console.log('[Tour] shouldShowTour → false (completed/skipped)');
      return false;
    }

    if (state.version !== TOUR_VERSION) {
      console.log('[Tour] shouldShowTour → true (versão diferente)');
      return true;
    }

    console.log('[Tour] shouldShowTour → true');
    return true;
  } catch {
    return true;
  }
}
